import {
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from "@tanstack/react-query";
import { QUERY_KEYS } from "./queryKeys";
import { AxiosResponse } from "axios";
import {
  ApiResponse,
  PaginationParams,
  SortingParams,
} from "@src/types/httpApi";
import {
  ICreateMoodBody,
  IMood,
  IMoodFactory,
  IMoodsFilters,
} from "@src/types/mood";
import {
  createMoodApi,
  fetchFactoryMoods,
  fetchMoods,
} from "@src/apis/moodsApis";
import httpApi from "@src/utils/httpApi";
import { RoleType } from "@src/types/user";

export const useInfiniteMoods = (
  filters: Partial<IMoodsFilters & PaginationParams & SortingParams>,
  options?: any
) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEYS.GET_MOODS, filters],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      fetchMoods({
        ...filters,
        sorting: {
          field: filters.field ?? "createdAt",
          direction: filters.direction ?? "desc",
        },
        page: pageParam,
        pageSize: 50,
        title: filters.title ?? "",
        description: filters.description ?? "",
        color: filters.color ?? "",
        platform: filters.platform ?? "web",
        searchText: filters.searchText ?? "",
      }),
    getNextPageParam: (
      lastPage: AxiosResponse<ApiResponse<IMood[]>> | undefined
    ) => {
      if (!lastPage || !lastPage?.data?.meta?.pagination) {
        return undefined;
      }
      try {
        const { page: currentPage, pageCount: totalPages } =
          lastPage.data.meta.pagination;
        return currentPage < totalPages ? currentPage + 1 : undefined;
      } catch {
        return undefined;
      }
    },
    ...options,
  });
};

export const useInfiniteFactoryMoods = (
  filters: Partial<IMoodsFilters & PaginationParams & SortingParams>,
  options?: any
) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEYS.GET_MOODS, filters],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      fetchFactoryMoods({
        ...filters,
        sorting: {
          field: filters.field ?? "createdAt",
          direction: filters.direction ?? "desc",
        },
        page: pageParam,
        pageSize: 100,
        title: "",
        description: "",
        color: "",
        platform: "",
        searchText: filters.searchText ?? "",
      }),
    getNextPageParam: (
      lastPage: AxiosResponse<ApiResponse<IMood[]>> | undefined
    ) => {
      if (!lastPage || !lastPage?.data?.meta?.pagination) {
        return undefined;
      }
      try {
        const { page: currentPage, pageCount: totalPages } =
          lastPage.data.meta.pagination;
        return currentPage < totalPages ? currentPage + 1 : undefined;
      } catch {
        return undefined;
      }
    },
    ...options,
  });
};

export const useCreateMoodMutation = (
  options?: UseMutationOptions<
    AxiosResponse<any>,
    Error,
    ICreateMoodBody,
    unknown
  >
): UseMutationResult<AxiosResponse<any>, Error, ICreateMoodBody, unknown> => {
  return useMutation<AxiosResponse<any>, Error, ICreateMoodBody>({
    mutationFn: (data: ICreateMoodBody) => createMoodApi(data),
    ...options,
  });
};

export const useMoodFactoryDetails = (
  moodFactoryId: string,
  role: RoleType
): UseQueryResult<IMoodFactory> => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_MOOD_FACTORY_DETAILS, moodFactoryId, role],
    queryFn: async () => {
      const { data } = await httpApi.get(
        `/api/mood-factories/${moodFactoryId}`,
        {
          params: {
            populate: {
              playlists: {
                fields: ["*"],
              },
            },
          },
        }
      );
      return data.data;
    },
    enabled: (!!moodFactoryId && role === "admin") || role === "manager",
  });
};
